import { Trans } from "@lingui/react/macro";
import {
  AlertTriangle,
  ArrowLeft,
  HelpCircle,
  RefreshCw,
  XCircle,
} from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router";

const CheckoutError: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();

  const handleRetryPayment = () => {
    if (bookingId) {
      navigate(`/checkout/${bookingId}`);
    }
  };

  const handleGoToBookings = () => {
    navigate(`/mystudio/bookings`);
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Error Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <XCircle className="h-10 w-10 text-red-600" />
        </div>
        <h1 className="mb-2 text-3xl font-bold text-gray-900">
          <Trans>Payment Failed</Trans>
        </h1>
        <p className="text-lg text-gray-600">
          <Trans>We encountered an issue processing your payment.</Trans>
        </p>
      </div>

      {/* Error Details */}
      <div className="mb-8 rounded-lg border-2 border-red-200 bg-red-50 p-6">
        <div className="mb-4 flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-red-600" />
          <h2 className="text-xl font-semibold text-red-900">
            <Trans>What happened?</Trans>
          </h2>
        </div>

        <div className="space-y-2 text-red-800">
          <p>
            <Trans>
              Your payment could not be processed successfully. This might be
              due to:
            </Trans>
          </p>
          <ul className="ml-4 list-inside list-disc space-y-1 text-red-700">
            <li>
              <Trans>Insufficient funds or declined card</Trans>
            </li>
            <li>
              <Trans>Incorrect payment details</Trans>
            </li>
            <li>
              <Trans>Network connection issues</Trans>
            </li>
            <li>
              <Trans>Temporary payment service interruption</Trans>
            </li>
          </ul>
        </div>

        <div className="mt-4 rounded-lg border border-red-200 bg-red-100 p-4">
          <p className="font-medium text-red-800">
            <Trans>Don't worry!</Trans>
          </p>
          <p className="mt-1 text-sm text-red-700">
            <Trans>
              No charges have been made to your account. Your booking is still
              pending and you can try again.
            </Trans>
          </p>
        </div>
      </div>

      {/* Troubleshooting */}
      <div className="mb-8 rounded-lg border border-gray-200 bg-gray-50 p-6">
        <div className="mb-4 flex items-center gap-2">
          <HelpCircle className="h-6 w-6 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            <Trans>Troubleshooting Steps</Trans>
          </h3>
        </div>
        <div className="space-y-2 text-gray-700">
          <p>
            •{" "}
            <Trans>
              Double-check your payment details (card number, expiry date, CVV)
            </Trans>
          </p>
          <p>
            • <Trans>Ensure you have sufficient funds available</Trans>
          </p>
          <p>
            • <Trans>Try using a different payment method</Trans>
          </p>
          <p>
            • <Trans>Check your internet connection and try again</Trans>
          </p>
          <p>
            •{" "}
            <Trans>
              Contact your bank if you suspect they may have blocked the
              transaction
            </Trans>
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-4">
        <div className="flex flex-col justify-center gap-4 sm:flex-row">
          {bookingId && (
            <button
              onClick={handleRetryPayment}
              className="bg-soluud-primary hover:bg-soluud-primary-hover active:bg-soluud-primary-active inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 font-medium text-white shadow-sm transition-all duration-150 active:scale-[0.98]"
            >
              <RefreshCw className="h-5 w-5" />
              <Trans>Try Payment Again</Trans>
            </button>
          )}

          <button
            onClick={handleGoToBookings}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-600 px-6 py-3 font-medium text-white transition-colors hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
            <Trans>Go to Booking Overview</Trans>
          </button>
        </div>

        <div className="text-center">
          <button
            onClick={handleGoHome}
            className="text-gray-600 underline transition-colors hover:text-gray-800"
          >
            <Trans>Return to Home Page</Trans>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutError;
